<template>
    <div>
        <div v-on:click="onClick">
            <pc-input
                v-bind:id="id"
                ref="typeSearch"
                v-bind:validation-group-id="validationGroupId"
                v-bind:model-value="model.line1"
                v-bind:group-id="groupId"
                v-bind:autocomplete="browserAutocompleteValue"
                v-bind:validator="validator"
                v-bind:readonly="readonly"
                v-bind:is-sub-label="true"
                v-bind:required="required"
                v-bind:label="label"
                v-bind:show-validation-errors="false"
                v-bind:placeholder="streetPlaceholder"
                v-bind:data-test-role="testRole"
                v-bind:external-native-handlers="{ keydown: onKey }"
                type="text"
                v-on:update:model-value="onUpdate"
                v-on:validated="$emit('validated', $event)" />
        </div>
        <address-popper
            v-if="useAutocomplete"
            ref="addrPopper"
            v-bind:suggestions="suggestions"
            v-bind:highlight-index="highlightIndex"
            v-on:autofill="autofill"
            v-on:update:highlight-index="(val) => (highlightIndex = val)" />
    </div>
</template>

<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/addressSubElementLayout.i18n";
    import addressPopper from "./address-popper.vue";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "../ProjectForm/projectFormControllerStore";
    import { simpleDebounce } from "Util/util";
    import { BROWSER_AUTOCOMPLETE_TOKEN } from "Util/browserAutocompleteConstants";

    export default {
        components: {
            addressPopper
        },
        props: {
            id: { type: String, required: true },
            groupId: { type: String, required: false, default: null },
            validationGroupId: { type: String, required: false, default: null },
            label: { type: String, required: false, default: null },
            required: { type: Boolean, required: true },
            readonly: { type: Boolean, required: true },
            model: { type: Object, required: true },
            validator: { type: Object, required: false, default: null },
            testRole: { type: String, required: true },
            // In this context, "autocomplete" refers to 3rd-party enabled predictive address entry (ie. Smarty)
            // This is not the same as the HTML autocomplete attribute.
            useAutocomplete: { type: Boolean, required: false, default: false },
            includeCounty: { type: Boolean, required: false, default: false },
            useLocationFilter: { type: Boolean, required: false, default: false }
        },
        emits: ["update", "validated", "autofill", "autofill-county"],
        data() {
            return {
                suggestions: null,
                expansionAddress: null,
                lastSearchText: "",
                nextSearchText: "",
                highlightIndex: -1
            };
        },
        computed: {
            browserAutocompleteValue() {
                // Attempt to suppress the built-in browser autocomplete if we're using our own predictive address
                // autocomplete. This is not guaranteed to work in all browsers.
                return this.useAutocomplete ? BROWSER_AUTOCOMPLETE_TOKEN.off : BROWSER_AUTOCOMPLETE_TOKEN.addressLine1;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        methods: {
            onKey($event) {
                if (!this.suggestions) {
                    return;
                }
                switch ($event.keyCode) {
                    case 38: // arrow up
                        if (this.highlightIndex > 0) {
                            this.highlightIndex -= 1;
                            $event.preventDefault();
                        }
                        break;
                    case 40: // arrow down
                        if (this.highlightIndex < this.suggestions.length - 1) {
                            this.highlightIndex += 1;
                            $event.preventDefault();
                        }
                        break;
                    case 13: // enter
                        if (this.highlightIndex !== -1) {
                            this.autofill(this.highlightIndex);
                            $event.target.blur();
                            $event.preventDefault();
                        }
                        break;
                    case 9: //tab
                    case 27: //escape
                        this.highlightIndex = -1;
                        this.$refs.addrPopper.popper_hide();
                        break;
                    case 33: // page up
                        if (this.highlightIndex !== -1) {
                            this.highlightIndex = Math.max(0, this.highlightIndex - 10);
                            $event.preventDefault();
                        }
                        break;
                    case 34: // page down
                        if (this.highlightIndex !== -1) {
                            this.highlightIndex = Math.min(this.suggestions.length - 1, this.highlightIndex + 10);
                            $event.preventDefault();
                        }
                        break;
                    case 35: // end
                        if (this.highlightIndex !== -1) {
                            this.highlightIndex = this.suggestions.length - 1;
                            $event.preventDefault();
                        }
                        break;
                    case 36: // home
                        if (this.highlightIndex !== -1) {
                            this.highlightIndex = 0;
                            $event.preventDefault();
                        }
                        break;
                }
            },
            async autofill(index) {
                if (this.suggestions[index].Secondary && this.suggestions[index].Entries > 1) {
                    this.expansionAddress = this.suggestions[index];
                    this.$emit("autofill", {
                        Street: `${this.expansionAddress.Street} ${this.expansionAddress.Secondary} `
                    });
                    this.suggestions = await this.getAddressExpansionSuggestions(this.suggestions[index]);
                    this.$refs.typeSearch.$refs.input.focus();
                } else {
                    this.expansionAddress = null;
                    this.$emit("autofill", this.suggestions[index]);
                    if (this.useAutocomplete && this.includeCounty) {
                        const county = await this.getAddressCountySuggestion(this.suggestions[index]);
                        this.$emit("autofill-county", county);
                    }
                    this.$refs.addrPopper.popper_hide();
                }
            },
            onClick() {
                if (this.useAutocomplete && (!this.model.city || !this.model.state || !this.model.zipcode)) {
                    this.highlightIndex = -1;
                    this.nextSearchText = this.model.line1;
                    this.displaySuggestions();
                }
            },
            async displaySuggestions() {
                if (!this.nextSearchText || !this.nextSearchText.trim()) {
                    this.$refs.addrPopper.popper_hide();
                    return;
                }
                if (this.lastSearchText !== this.nextSearchText) {
                    this.lastSearchText = this.nextSearchText;
                    if (this.expansionAddress && this.nextSearchText.startsWith(this.expansionAddress.Street)) {
                        this.expansionAddress.Secondary = this.nextSearchText
                            .slice(this.expansionAddress.Street.length)
                            .trim();
                        this.expansionAddress.Entries = "";
                        this.suggestions = await this.getAddressExpansionSuggestions(this.expansionAddress);
                    } else {
                        this.suggestions = await this.getAddressSuggestions({ Street: this.nextSearchText });
                    }
                }
                if (this.suggestions && this.suggestions.length > 0) {
                    this.$refs.addrPopper.popper_show(this.$refs.typeSearch.$el);
                }
            },
            async getAddressSuggestions(suggestion) {
                const result = await this.$store.dispatch(
                    PROJECT_FORM_CONTROLLER_TYPES.actions.getAddressAutocomplete,
                    {
                        suggestion: suggestion,
                        useLocationFilter: this.useLocationFilter
                    }
                );
                return result.Data.AddressSuggestions;
            },
            async getAddressExpansionSuggestions(suggestion) {
                const result = await this.$store.dispatch(
                    PROJECT_FORM_CONTROLLER_TYPES.actions.getAddressExpansionAutocomplete,
                    {
                        suggestion: suggestion
                    }
                );
                return result.Data.AddressSuggestions;
            },
            async getAddressCountySuggestion(suggestion) {
                const result = await this.$store.dispatch(
                    PROJECT_FORM_CONTROLLER_TYPES.actions.getAddressCountyAutocomplete,
                    {
                        suggestion: suggestion
                    }
                );
                return result.Data.CountySuggestion;
            },
            onUpdate(value) {
                this.$emit("update", value);
                if (this.useAutocomplete) {
                    this.highlightIndex = -1;
                    const trimmedValue = value.trim();
                    this.nextSearchText = trimmedValue.length >= 3 ? trimmedValue : "";
                    simpleDebounce(this.displaySuggestions, 500)();
                }
            }
        }
    };
</script>
